import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { Alert, AlertDescription } from "../../../ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import {
  Brain,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Check,
  XCircle,
  DollarSign,
  Calendar,
  BarChart3,
  Info,
  Loader2,
  PauseCircle,
  RotateCcw,
  CornerDownRight,
} from "lucide-react";
import { cn } from "../../../utils/UnderwritingEngineV2/utils";
import { generatePDF } from "../../../utils/UnderwritingEngineV2/pdfGenerator";
import { buildOriginalValuesElement } from "../../../utils/UnderwritingEngineV2/pdfOriginalValues";
import { LOAN_AMOUNT_MAX, MODEL_SCORING_MAX } from "../../../utils/UnderwritingEngineV2/corpScoringGrid";
import axios from "axios";
import UserService from "../../../services/UserService";
import { logWorkflowAction } from "../../../utils/workflowLogger";

const serverConfig = require("../../../config/server.js");

// athena_status vocabulary (loanStatus prop is the athena_status value, or "Referred").
const TERMINAL_ATHENA_STATUSES = new Set(["Approved", "Rejected", "Withdrawn", "Syndicate"]);
const HOLD_ATHENA_STATUSES = new Set(["Credit Hold", "Hold"]);
const isTerminalStatus = (s: string | null | undefined): boolean => !!s && TERMINAL_ATHENA_STATUSES.has(s);
const isHoldStatus = (s: string | null | undefined): boolean => !!s && HOLD_ATHENA_STATUSES.has(s);

interface UnderwriteProps {
  applicationInputs: any;
  originalBackendData?: Record<string, any>;
  setCurrentStep: (
    step: "Loans" | "Application" | "Score" | "Review" | "Underwrite",
  ) => void;
  loanStatus?: string | null;
  userCreditLimit?: number | null;
  /** Called after a successful decision API + unlock; refresh loans list before navigating to Loans. */
  onAfterDecision?: () => void | Promise<void>;
  /** Called when the lock check fails before submitting a decision; show error in the parent. */
  onLockError?: (msg: string) => void;
  /** Called when the session has been taken over or expired; shows modal in parent. */
  onSessionTaken?: (msg: string) => void;
}

const Underwrite = ({
  applicationInputs,
  originalBackendData = {},
  setCurrentStep,
  loanStatus = null,
  userCreditLimit = null,
  onAfterDecision,
  onLockError,
  onSessionTaken,
}: UnderwriteProps) => {
  const [loanUnderwrite, setLoanUnderwrite] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [loanId, setLoanId] = useState("");
  const [lookalikeAll, setLookalikeAll] = useState<any[]>([]);
  const [lookalikePerforming, setLookalikePerforming] = useState<any[]>([]);
  const [lookalikeNonPerforming, setLookalikeNonPerforming] = useState<any[]>([]);
  const [lookalikeRejected, setLookalikeRejected] = useState<any[]>([]);
  const [lookalikeSyndicated, setLookalikeSyndicated] = useState<any[]>([]);
  const [lookalikeWithdrawn, setLookalikeWithdrawn] = useState<any[]>([]);
  const [lookalikeAssetType, setLookalikeAssetType] = useState<any[]>([]);
  const [lookalikeLV, setLookalikeLV] = useState<any[]>([]);
  const [selectedLookalikeType, setSelectedLookalikeType] = useState<
    "all" | "asset" | "lv"
  >("all");
  const [lookalikeFilter, setLookalikeFilter] = useState<"default" | "performing" | "non_performing" | "rejected" | "syndicated" | "withdrawn">("default");
  const [industryOptions, setIndustryOptions] = useState<string[]>([]);
  const [assetTypeOptions, setAssetTypeOptions] = useState<string[]>([]);
  const [filteredLookalike, setFilteredLookalike] = useState<any>(null);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const lastPayloadRef = useRef<any>(null);
  const [activeFilterType, setActiveFilterType] = useState<"industry" | "assetType" | null>(null);
  const [activeFilterValue, setActiveFilterValue] = useState<string | null>(null);
  const [industrySearchText, setIndustrySearchText] = useState("");
  const [industrySuggestions, setIndustrySuggestions] = useState<string[]>([]);
  const [industrySearchOpen, setIndustrySearchOpen] = useState(false);
  const [industryHighlightIdx, setIndustryHighlightIdx] = useState(-1);
  const [assetTypeSearchText, setAssetTypeSearchText] = useState("");
  const [assetTypeSuggestions, setAssetTypeSuggestions] = useState<string[]>([]);
  const [assetTypeSearchOpen, setAssetTypeSearchOpen] = useState(false);
  const [assetTypeHighlightIdx, setAssetTypeHighlightIdx] = useState(-1);
  const [revealDetails, setRevealDetails] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lookalikeCounts, setLookalikeCounts] = useState<{
    all: number;
    asset: number;
    lv: number;
    total_matched?: number;
  }>({
    all: 0,
    asset: 0,
    lv: 0,
  });
  const [lookalikeStats, setLookalikeStats] = useState<{
    performing_pct: number | null;
    performing_count: number | null;
    non_performing_pct: number | null;
    non_performing_count: number | null;
    rejected_count: number | null;
    rejected_pct: number | null;
    withdrawn_count: number | null;
    withdrawn_pct: number | null;
    syndicated_count: number | null;
    syndicated_pct: number | null;
    booked_amount: number | null;
    funding_count: number | null;
    net_loss: number | null;
    net_loss_pct: number | null;
  }>({ performing_pct: null, performing_count: null, non_performing_pct: null, non_performing_count: null, rejected_count: null, rejected_pct: null, withdrawn_count: null, withdrawn_pct: null, syndicated_count: null, syndicated_pct: null, booked_amount: null, funding_count: null, net_loss: null, net_loss_pct: null });
  const sessionAlertShownRef = React.useRef(false);

  const clearColumnFilter = () => {
    setActiveFilterType(null);
    setActiveFilterValue(null);
    setFilteredLookalike(null);
    setIndustrySearchText("");
    setIndustrySearchOpen(false);
    setIndustrySuggestions([]);
    setIndustryHighlightIdx(-1);
    setAssetTypeSearchText("");
    setAssetTypeSearchOpen(false);
    setAssetTypeSuggestions([]);
    setAssetTypeHighlightIdx(-1);
  };

  const applyColumnFilter = async (type: "industry" | "assetType", value: string) => {
    if (!lastPayloadRef.current) return;
    clearColumnFilter();
    setActiveFilterType(type);
    setActiveFilterValue(value);
    setIsFilterLoading(true);
    try {
      const param = type === "industry"
        ? `?industry=${encodeURIComponent(value)}`
        : `?asset_type=${encodeURIComponent(value)}`;
      const res = await axios.post(
        `${serverConfig.api_base_url}underwriting-engine/underwrite${param}`,
        lastPayloadRef.current,
        { headers: { "Content-Type": "application/json" }, timeout: 120000 }
      );
      setFilteredLookalike(res.data.lookalike ?? null);
    } catch (e) {
      console.error("Filter call failed", e);
      setActiveFilterType(null);
      setActiveFilterValue(null);
    } finally {
      setIsFilterLoading(false);
    }
  };

  // Industry suggestions — starts-with, 200ms debounce (scans lightweight string list)
  useEffect(() => {
    if (!industrySearchText) { setIndustrySuggestions([]); return; }
    const t = setTimeout(() => {
      const lower = industrySearchText.toLowerCase();
      setIndustrySuggestions(industryOptions.filter(v => v.toLowerCase().startsWith(lower)).sort());
    }, 200);
    return () => clearTimeout(t);
  }, [industrySearchText, industryOptions]);

  // Asset Type suggestions — starts-with, 200ms debounce (scans lightweight string list)
  useEffect(() => {
    if (!assetTypeSearchText) { setAssetTypeSuggestions([]); return; }
    const t = setTimeout(() => {
      const lower = assetTypeSearchText.toLowerCase();
      setAssetTypeSuggestions(assetTypeOptions.filter(v => v.toLowerCase().startsWith(lower)).sort());
    }, 200);
    return () => clearTimeout(t);
  }, [assetTypeSearchText, assetTypeOptions]);

  const activeStats = filteredLookalike ? filteredLookalike.stats : lookalikeStats;

  const handleSessionExpired = () => {
    if (sessionAlertShownRef.current) {
      return;
    }
    sessionAlertShownRef.current = true;
    onSessionTaken?.("Your session has expired. Click OK to return to the loan queue.");
    setCurrentStep("Loans");
  };

  // Helper functions for lookalike data
  function transformLookalikeData(data: any) {
    if (!data || typeof data !== "object") return [];
    // Get all application IDs from one of the keys (e.g., from the first column)
    const firstKey = Object.keys(data)[0];
    if (!firstKey) return [];
    const appIds = Object.keys(data[firstKey] || {});

    // Build an array of row objects
    return appIds.map((appId) => {
      const row: any = { appId }; // start with application ID
      for (const col in data) {
        row[col] = data[col][appId];
      }
      return row;
    });
  }

  const getDisplayedData = () => {
    if (selectedLookalikeType === "asset") return lookalikeAssetType;
    if (selectedLookalikeType === "lv") return lookalikeLV;
    return lookalikeAll;
  };

  // Calculate internal rating
  const calculateInternalRating = () => {
    if (applicationInputs.anyMajorDerog) {
      return {
        score: "—",
        rating: "—",
        type: applicationInputs.usePGSwitch ? "PG" : applicationInputs.useCorpSwitch ? "Corp" : "",
        decisionFlag: "Major Derog",
      };
    }
    if (applicationInputs.usePGSwitch) {
      return {
        score: applicationInputs.pgScore || 0,
        rating: applicationInputs.pgRating || "N/A",
        type: "PG",
        decisionFlag: applicationInputs.pgRating === "C" ? "Syndicate" : "",
      };
    }
    if (applicationInputs.useCorpSwitch) {
      return {
        score: applicationInputs.corpScore || 0,
        rating: applicationInputs.corpRating || "N/A",
        type: "Corp",
        decisionFlag: applicationInputs.corpRating === "C" ? "Syndicate" : "",
      };
    }
    return { score: 0, rating: "N/A", type: "", decisionFlag: "" };
  };

  const internalRating = calculateInternalRating();

  // Athena recommendation now comes straight from the engine (athena_score vs the
  // pass threshold). No more recomputing from strict_proba.
  const getAthenaRecommendation = () => {
    if (internalRating.decisionFlag === "Major Derog") return "Decline";
    const rec = loanUnderwrite.athena_recommendation;
    if (rec === undefined || rec === null || rec === "") return "None";
    return rec; // "Approve" | "Decline" | "None"
  };

  const getRiskScoreHint = () => {
    // Gated apps carry the guardrail reason in na_reason; otherwise show the bar.
    if (loanUnderwrite.na_reason) return `(${loanUnderwrite.na_reason})`;
    return "(Must be > 650)";
  };

  // Calculate additional metrics
  const calculateMetrics = () => {
    return {
      yield:
        applicationInputs.yield_fe && applicationInputs.yield_fe !== "NA"
          ? `${(Number(applicationInputs.yield_fe) * 100).toFixed(2)}%`
          : "N/A",
      riskScore:
        typeof loanUnderwrite.athena_score === "number"
          ? Math.round(loanUnderwrite.athena_score).toString()
          : "N/A",
      riskImpact:
        loanUnderwrite.athena_risk_impact != null &&
        loanUnderwrite.athena_risk_impact !== ""
          ? loanUnderwrite.athena_risk_impact
          : "N/A",
    };
  };

  const metrics = calculateMetrics();

  const downloadPDF = useCallback(async () => {
    setPdfGenerating(true);
    const pgEl = document.getElementById("pdf-pg-scoring");
    const corpEl = document.getElementById("pdf-corp-scoring");
    const appDetailsEl = document.getElementById("application-details");

    const prev: { el: HTMLElement; display: string }[] = [];
    const hide = (el: HTMLElement | null) => {
      if (!el) return;
      prev.push({ el, display: el.style.display });
      el.style.display = "none";
    };
    const restore = () => {
      prev.forEach(({ el, display }) => {
        el.style.display = display;
      });
    };

    const hadAppDetailsHidden =
      appDetailsEl?.classList.contains("hidden") ?? false;
    if (hadAppDetailsHidden && appDetailsEl) {
      appDetailsEl.classList.remove("hidden");
    }

    let originalValuesEl: HTMLElement | null = null;

    try {
      // Wait for the pdfGenerating spinner (z-[100]) to render before we append
      // the third-page element (z-index:99). This guarantees it's hidden behind
      // the spinner so the user never sees it flash on screen.
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });

      // "Loan Application Data Changes" page = the frozen intake snapshot vs the
      // current uw_working values on the application-data fields, where they differ.
      // Computed in the engine from uw_contracts.intake_snapshot. The reason column
      // comes from the underwriter's notes in uw_field_history (per field).
      let diffRows: Array<{ field: string; label: string; intake: any; current: any }> = [];
      try {
        const diffRes = await axios.get(
          `${serverConfig.api_base_url}underwriting-engine/app-data-diff/${applicationInputs.applicationNumber}`
        );
        if (diffRes.data?.status === "success") diffRows = diffRes.data.data || [];
      } catch (err) {
        console.error("[PDF] failed to fetch app-data diff:", err);
      }

      // Per-field change reasons (keyed by UI field name) from uw_field_history.
      let fieldNotes: Record<string, string> = {};
      try {
        const histRes = await axios.get(
          `${serverConfig.api_base_url}underwriting-engine/field-history/${applicationInputs.applicationNumber}`
        );
        if (histRes.data?.status === "success" && histRes.data.notes && typeof histRes.data.notes === "object") {
          fieldNotes = histRes.data.notes;
        }
      } catch (err) {
        console.error("[PDF] failed to fetch field-history notes:", err);
      }

      const hasContent = diffRows.length > 0;
      if (hasContent) {
        // Reshape diff rows to the {field: {label, previousVal, newVal}} the builder expects.
        const exceptions: Record<string, { label: string; previousVal: any; newVal: any }> = {};
        for (const r of diffRows) {
          exceptions[r.field] = { label: r.label, previousVal: r.intake, newVal: r.current };
        }
        try {
          originalValuesEl = buildOriginalValuesElement(exceptions, fieldNotes);
          document.body.appendChild(originalValuesEl);
          console.log("[PDF] third page appended, changed fields:", diffRows.map((r) => r.field));
          // One extra frame to let the browser fully lay out the appended element
          await new Promise<void>((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
          });
        } catch (err) {
          console.error("[PDF] failed to build third page element:", err);
          originalValuesEl = null;
        }
      } else {
        console.log("[PDF] no application-data changes or notes — PDF will have 2 pages only");
      }

      if (applicationInputs.usePGSwitch) {
        hide(corpEl);
      } else if (applicationInputs.useCorpSwitch) {
        hide(pgEl);
      } else {
        hide(pgEl);
        hide(corpEl);
      }

      const pdfIds = ["application-details", "underwrite-decision"];
      if (originalValuesEl) pdfIds.push("pdf-original-values");

      const showWatermark = process.env.REACT_APP_WATERMARK_FEATURE === "uat";

      await generatePDF(
        pdfIds,
        `${applicationInputs.applicationNumber || "underwrite"}-Athena`,
        "hsl(218, 30%, 8%)",
        undefined,
        showWatermark,
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      restore();
      if (hadAppDetailsHidden && appDetailsEl) {
        appDetailsEl.classList.add("hidden");
      }
      if (originalValuesEl) {
        document.body.removeChild(originalValuesEl);
        originalValuesEl = null;
      }
      setPdfGenerating(false);
    }
  }, [applicationInputs]);

  const handleUnderwrite = async () => {
    setLoading(true);
    setShowRecommendations(false);
    let underwriteSucceeded = false;

    try {
      // Calculate decision flag and rating/score
      let decisionFlag = "";
      let rating = "";
      let score = "";
      let scoreType = "";

      if (applicationInputs.usePGSwitch) {
        rating = applicationInputs.pgRating || "";
        score = String(applicationInputs.pgScore || 0);
        scoreType = "PG";
        if (rating === "C") {
          decisionFlag = "Syndicate";
        }
      } else if (applicationInputs.useCorpSwitch) {
        if (applicationInputs.anyMajorDerog) {
          decisionFlag = "Major Derog";
          rating = "NA";
          score = "NA";
        } else {
          rating = applicationInputs.corpRating || "";
          score = String(applicationInputs.corpScore || 0);
          scoreType = "Corp";
          if (rating === "C") {
            decisionFlag = "Syndicate";
          }
        }
      }

      // LW returns negative values for absent fields — map them to "" (no data)
      const lwEmpty = (v: any): any =>
        v != null && !Number.isNaN(Number(v)) && Number(v) < 0 ? "" : v;

      // Build comprehensive payload matching old code exactly
      const payload: any = {
        // Original backend field names (matching old JSX defaults)
        AVG_HI_CREDIT: applicationInputs.avgHighCredit || 0,
        COM_CRE_SCORE: applicationInputs.commercialCreditScore || 0,
        EMP_TOT: applicationInputs.numberOfEmployees || 0,
        LW_YRS_IN_BUS: applicationInputs.timeInBusiness || 0,
        NUM_INQ: applicationInputs.noOfInquiries || 0,
        NUM_TRADE_EXP: applicationInputs.noOfTradeExperiences || 0,
        NUMDELINQ: applicationInputs.noOfDelinquencies || 0,
        PAYDEX_FIRM: applicationInputs.paydex || 0,
        PN_MASTER_SCR: applicationInputs.pnMasterScore || 0,
        WASDELINQ: applicationInputs.noOfTotalDelinquencies || 0,
        udf_fico: lwEmpty(applicationInputs.udf_fico),
        udf_fico_average: lwEmpty(applicationInputs.udf_fico_average),
        udf_fico_max: lwEmpty(applicationInputs.udf_fico_max),
        BANKRUPTCY: applicationInputs.bankruptcy == 1 ? 1 : 0,
        JUDGEMENTS: applicationInputs.judgements == "J" ? 1 : 0,
        LIENS: applicationInputs.liens == "L" ? 1 : 0,
        SUITS: applicationInputs.suits == "S" ? 1 : 0,
        Asset_Type: applicationInputs.assetType || "OTHER",
        Application_Number: applicationInputs.applicationNumber || 0,
        Submit_Date:
          applicationInputs.submitDate ||
          new Date().toISOString().split("T")[0],
        Number_Of_Guarantors: applicationInputs.noOfGuarantors || 0,
        Submit_Cost: applicationInputs.loanAmount || 0,
        Is_New_Application: applicationInputs.isNewApplication ? 1 : 0,

        // Basic application fields (camelCase)
        isNewApplication: applicationInputs.isNewApplication || false,
        applicationNumber: Number(applicationInputs.applicationNumber || 0),
        nameOfBusiness: applicationInputs.nameOfBusiness || "",
        officeName: applicationInputs.officeName || "",
        loanAmount: String(applicationInputs.loanAmount || 0),
        assetType: applicationInputs.assetType || "OTHER",
        city: applicationInputs.city || "",

        // Bureau Details
        avgHighCredit: applicationInputs.avgHighCredit || 0,
        bankruptcy: applicationInputs.bankruptcy == "<NA>" ? 0 : 1,
        birthyear: applicationInputs.birthyear || 0,
        commercialCreditScore: applicationInputs.commercialCreditScore || 0,
        numberOfEmployees: applicationInputs.numberOfEmployees || 0,
        noOfGuarantors: applicationInputs.noOfGuarantors || 0,
        suits: applicationInputs.suits == "<NA>" ? 0 : 1,
        liens: applicationInputs.liens == "<NA>" ? 0 : 1,
        judgements: applicationInputs.judgements == "<NA>" ? 0 : 1,
        timeInBusiness: applicationInputs.timeInBusiness || 0,
        noOfDelinquencies: applicationInputs.noOfDelinquencies || 0,
        noOfInquiries: applicationInputs.noOfInquiries || 0,
        noOfTradeExperiences: applicationInputs.noOfTradeExperiences || 0,
        paydex: applicationInputs.paydex || 0,
        pnMasterScore: applicationInputs.pnMasterScore || 0,
        realStateBalance: Math.max(0, Number(applicationInputs.realStateBalance) || 0),
        noOfSatisfactoryAccounts:
          applicationInputs.noOfSatisfactoryAccounts || 0,
        standardCode: applicationInputs.standardCode || "",
        startYear: applicationInputs.startYear || 0,
        totalRevolvingBalance: applicationInputs.totalRevolvingBalance || 0,
        totalRevolvingAvailable: applicationInputs.totalRevolvingAvailable || 0,
        vantage: applicationInputs.vantage || 0,
        noOfTotalDelinquencies: applicationInputs.noOfTotalDelinquencies || 0,
        yield_old: applicationInputs.yield || 0,
        optimismIndex: Number(applicationInputs.optimismIndex) || 0,
        gdpGrowthRate: Number(applicationInputs.gdpGrowthRate) || 0,
        cpiIndex: Number(applicationInputs.cpiIndex) || 0,
        fedFundsRate: Number(applicationInputs.fedFundsRate) || 0,
        industrialProductionIndex:
          Number(applicationInputs.industrialProductionIndex) || 0,
        unemploymentRate: Number(applicationInputs.unemploymentRate) || 0,
        cpiMoM: Number(applicationInputs.cpiMoM) || 0,
        cpiRoll3M: Number(applicationInputs.cpiRoll3M) || 0,
        gdpRoll3Q: Number(applicationInputs.gdpRoll3Q) || 0,
        unemploymentRoll3M: Number(applicationInputs.unemploymentRoll3M) || 0,
        optimismRoll6M: Number(applicationInputs.optimismRoll6M) || 0,
        industrialProdChange6M:
          Number(applicationInputs.industrialProdChange6M) || 0,
        covidFlag: parseInt(applicationInputs.covidFlag, 10) || 0,
        optimismIndexNormalized:
          Number(applicationInputs.optimismIndexNormalized) || 0,
        unemploymentRateNormalized:
          Number(applicationInputs.unemploymentRateNormalized) || 0,
        gdpGrowthRateNormalized:
          Number(applicationInputs.gdpGrowthRateNormalized) || 0,

        // PG fields
        pgTimeInBusiness: applicationInputs.pgTimeInBusiness || 0,
        pgFicoScore: applicationInputs.pgFicoScore || 0,
        pgStrongPaymentHistorySwitch:
          applicationInputs.pgStrongPaymentHistorySwitch || false,
        pgStrongPaymentHistorySwitchPoints:
          applicationInputs.pgStrongPaymentHistorySwitchPoints || 0,
        pgAcceptableCreditHistorySwitch:
          applicationInputs.pgAcceptableCreditHistorySwitch || false,
        pgAcceptableCreditHistorySwitchPoints:
          applicationInputs.pgAcceptableCreditHistorySwitchPoints || 0,
        pgStrongComparibleCreditSwitch:
          applicationInputs.pgStrongComparibleCreditSwitch || false,
        pgStrongComparibleCreditSwitchPoints:
          applicationInputs.pgStrongComparibleCreditSwitchPoints || 0,
        pgAcceptableComparibleCreditSwitch:
          applicationInputs.pgAcceptableComparibleCreditSwitch || false,
        pgAcceptableComparibleCreditSwitchPoints:
          applicationInputs.pgAcceptableComparibleCreditSwitchPoints || 0,
        pgPreferredCollateralSwitch:
          applicationInputs.pgPreferredCollateralSwitch || false,
        pgPreferredCollateralSwitchPoints:
          applicationInputs.pgPreferredCollateralSwitchPoints || 0,
        pgSoftCollateralSwitch:
          applicationInputs.pgSoftCollateralSwitch || false,
        pgSoftCollateralSwitchPoints:
          applicationInputs.pgSoftCollateralSwitchPoints || 0,
        pgIntangiblesSwitch: applicationInputs.pgIntangiblesSwitch || false,
        pgIntangiblesPoints: applicationInputs.pgIntangiblesPoints || 0,
        pgOverrideSwitch: applicationInputs.pgOverrideSwitch || false,
        pgOverridePoints: applicationInputs.pgOverridePoints || 0,
        pgOverrideText: applicationInputs.pgOverrideText || "",
        pgVendorCheck: applicationInputs.pgVendorCheck || false,
        pgVendorSupportSwitch: applicationInputs.pgVendorSupportSwitch || false,

        // Corp fields
        corpTimeInBusiness: applicationInputs.corpTimeInBusiness || 0,
        corpTimeInBusinessPoints:
          applicationInputs.corpTimeInBusinessPoints || 0,
        corpNumberOfEmployees: applicationInputs.corpNumberOfEmployees || 0,
        corpNumberOfEmployeesPoints:
          applicationInputs.corpNumberOfEmployeesPoints || 0,
        corpHighCredit: applicationInputs.corpHighCredit || 0,
        corpHighCreditPoints: applicationInputs.corpHighCreditPoints || 0,
        corpPaydex: applicationInputs.corpPaydex || 0,
        corpPaydexPoints: applicationInputs.corpPaydexPoints ?? 0,
        corpTradeLines: applicationInputs.corpTradeLines || 0,
        corpTradeLinesPoints: applicationInputs.corpTradeLinesPoints || 0,
        suitsLiensJudgements: applicationInputs.suitsLiensJudgements || "",
        suitsLiensJudgementsPoints:
          applicationInputs.suitsLiensJudgementsPoints || 1,
        historyClearSwitch: applicationInputs.historyClearSwitch || false,
        historyClearPoints: applicationInputs.historyClearPoints || 1,
        uccFilingsSwitch: applicationInputs.uccFilingsSwitch || false,
        uccFilingsPoints: applicationInputs.uccFilingsPoints || 1,
        highRiskIndustrySwitch:
          applicationInputs.highRiskIndustrySwitch || false,
        highRiskIndustryPoints: applicationInputs.highRiskIndustryPoints || -1,
        comparableLeaseSwitch: applicationInputs.comparableLeaseSwitch || false,
        comparableLeasePoints: applicationInputs.comparableLeasePoints || 1,
        majorityEquitySingleOwnershipSwitch:
          applicationInputs.majorityEquitySingleOwnershipSwitch || false,
        majorityEquitySingleOwnershipPoints:
          applicationInputs.majorityEquitySingleOwnershipPoints || -2,
        preferredCollateralSwitch:
          applicationInputs.preferredCollateralSwitch || false,
        preferredCollateralPoints:
          applicationInputs.preferredCollateralPoints || 1,
        softCollateralSwitch: applicationInputs.softCollateralSwitch || false,
        softCollateralPoints: applicationInputs.softCollateralPoints || -1,
        intangiblesSwitch: applicationInputs.intangiblesSwitch || false,
        intangiblesPoints: applicationInputs.intangiblesPoints || 0,
        overrideSwitch: applicationInputs.overrideSwitch || false,
        overridePoints: applicationInputs.overridePoints || 0,
        overrideText: applicationInputs.overrideText || "",
        anyMajorDerog: applicationInputs.anyMajorDerog || false,
        vendorCheck: applicationInputs.vendorCheck || false,

        // Ratings
        pgScore: applicationInputs.pgScore || 0,
        pgRating: applicationInputs.pgRating || "",
        corpScore: applicationInputs.corpScore || 0,
        corpRating: applicationInputs.corpRating || "",
        decisionFlag: decisionFlag,
        actualRating: applicationInputs.isNewApplication
          ? String(rating)
          : String(applicationInputs.actualRating || ""),
        actualScore: applicationInputs.isNewApplication
          ? String(score)
          : String(applicationInputs.actualScore || ""),
        scoreType: scoreType,

        // Criteria flags
        requiresPersonalGuarantor:
          applicationInputs.requiresPersonalGuarantor || 0,
        corpThreeOfFourCriteriaFailed:
          applicationInputs.corpThreeOfFourCriteriaFailed || 0,
        corpOneOfTwoCriteriaFailed:
          applicationInputs.corpOneOfTwoCriteriaFailed || 0,
        loanShortfallSupportFailed:
          applicationInputs.loanShortfallSupportFailed || 0,
      };

      // Add token to payload (as per backend expectation)
      const token = UserService.getToken();
      if (token) {
        payload.token = token;
      }

      console.log("Underwrite payload:", payload);

      try {
        const response = await axios.post(
          `${serverConfig.api_base_url}underwriting-engine/underwrite`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
            timeout: 120000, // 120s — covers cold model-load + remote-DB persist on first underwrite
          },
        );

        console.log("loan_underwrite response", response.data);

        if (response.data.status === "success") {
          underwriteSucceeded = true;
          setLoanId(response.data.id);
          setLoanUnderwrite(response.data.prediction || {});

          // Handle lookalike data - check if it's already transformed or needs transformation
          if (response.data.lookalike) {
            // Check if lookalike data is already an array (transformed) or needs transformation
            const allSimilar = Array.isArray(
              response.data.lookalike.all_similar,
            )
              ? response.data.lookalike.all_similar
              : transformLookalikeData(response.data.lookalike.all_similar);
            const assetSimilar = Array.isArray(
              response.data.lookalike.asset_similar,
            )
              ? response.data.lookalike.asset_similar
              : transformLookalikeData(response.data.lookalike.asset_similar);
            const lvSimilar = Array.isArray(response.data.lookalike.lv_similar)
              ? response.data.lookalike.lv_similar
              : transformLookalikeData(response.data.lookalike.lv_similar);

            setLookalikeAll(allSimilar);
            setLookalikePerforming(response.data.lookalike.performing_matches ?? []);
            setLookalikeNonPerforming(response.data.lookalike.non_performing_matches ?? []);
            setLookalikeRejected(response.data.lookalike.rejected_matches ?? []);
            setLookalikeSyndicated(response.data.lookalike.syndicated_matches ?? []);
            setLookalikeWithdrawn(response.data.lookalike.withdrawn_matches ?? []);
            setLookalikeFilter("default");
            lastPayloadRef.current = payload;
            setIndustryOptions(response.data.lookalike?.unique_industries ?? []);
            setAssetTypeOptions(response.data.lookalike?.unique_asset_types ?? []);
            setFilteredLookalike(null);
            clearColumnFilter();
            setLookalikeAssetType(assetSimilar);
            setLookalikeLV(lvSimilar);
            setLookalikeCounts(
              response.data.lookalike?.counts || { all: 0, asset: 0, lv: 0 },
            );
            setLookalikeStats(
              response.data.lookalike?.stats || { performing_pct: null, performing_count: null, non_performing_pct: null, non_performing_count: null, rejected_count: null, rejected_pct: null, withdrawn_count: null, withdrawn_pct: null, syndicated_count: null, syndicated_pct: null, booked_amount: null, funding_count: null, net_loss: null, net_loss_pct: null },
            );
            setRevealDetails(true);
          }
        } else if (response.data.status === 301) {
          handleSessionExpired();
          setLoanUnderwrite({});
        } else {
          console.error("API returned error:", response.data.message);
          setLoanUnderwrite({});
        }
      } catch (apiErr: any) {
        console.error("Error calling underwrite API:", apiErr);
        if (
          apiErr.response?.status === 401 ||
          apiErr.response?.status === 301 ||
          apiErr.response?.data?.status === 301
        ) {
          handleSessionExpired();
          return;
        }
        if (apiErr.response?.status === 423) {
          onSessionTaken?.(
            apiErr.response?.data?.detail ||
              "This loan is currently locked by another user.",
          );
        }
        setLoanUnderwrite({});
      }
    } catch (err) {
      console.error("Error in handleUnderwrite:", err);
      setLoanUnderwrite({});
    }

    // Show recommendations after a brief delay for animation, then auto-download PDF on success
    setTimeout(async () => {
      setShowRecommendations(true);
      setLoading(false);
      if (!underwriteSucceeded) {
        return;
      }
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      await new Promise<void>((resolve) => setTimeout(resolve, 100));
      try {
        await downloadPDF();
      } catch (pdfErr) {
        console.error("Error generating PDF after reviewing :", pdfErr);
      }
    }, 1500);
  };

  // Removed automatic call - now triggered by button click

  const unlockLoan = async () => {
    let token: string | undefined;
    try {
      token = await UserService.ensureFreshToken();
    } catch {
      UserService.doLogout();
      return;
    }
    const applicationNumber = Number(applicationInputs.applicationNumber || 0);
    if (!applicationNumber) {
      return;
    }
    await axios.post(
      `${serverConfig.api_base_url}underwriting-engine/loans/${applicationNumber}/unlock`,
      { token },
    );
  };

  const sendDecision = async (decision: string) => {
    setIsSubmitting(true);
    let token: string | undefined;
    try {
      token = await UserService.ensureFreshToken();
    } catch {
      UserService.doLogout();
      setIsSubmitting(false);
      return;
    }
    const applicationNumber = Number(applicationInputs.applicationNumber || 0);
    try {
      await axios.post(
        `${serverConfig.api_base_url}underwriting-engine/loans/${applicationNumber}/lock`,
        { token },
      );
    } catch (err: any) {
      if (err?.response?.status === 401 || err?.response?.status === 301) {
        handleSessionExpired();
        return;
      }
      const msg =
        err?.response?.status === 423
          ? err?.response?.data?.detail || "This loan is currently being processed by another user."
          : "Unable to verify lock ownership. Please try again.";
      onLockError?.(msg);
      setCurrentStep("Loans");
      return;
    }
    try {
      await axios.post(
        `${serverConfig.api_base_url}underwriting-engine/decision`,
        {
          id: loanId ? Number(loanId) : null,
          decision: decision,
          applicationNumber: Number(applicationInputs.applicationNumber || 0),
          formData: applicationInputs,
          token,
          hold_reason: "",
        },
      );
      await logWorkflowAction(
        applicationInputs.applicationNumber,
        "decision",
        applicationInputs,
      );
      try {
        await unlockLoan();
      } catch (unlockErr) {
        console.error("Error unlocking loan after decision:", unlockErr);
      }
      try {
        await onAfterDecision?.();
      } catch (refreshErr) {
        console.error("Error refreshing loans list after decision:", refreshErr);
      }
      setCurrentStep("Loans");
    } catch (err) {
      console.error("Error saving decision:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (value: number | string) => {
    if (value === "N/A" || value === undefined || value === null) {
      return "N/A";
    }
    if (typeof value === "string") {
      return value;
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };


  return (
    <div className="space-y-6">
      <div
        id="underwrite-decision"
        className="grid w-full max-w-full grid-cols-1 gap-6 lg:grid-cols-2"
      >
        {/* Left Side: Internal Rating */}
        <Card>
          <CardHeader>
            <CardTitle className="label-athena flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Internal Rating
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-muted-foreground">
                  Application Number
                </label>
                <div className="text-2xl font-bold mt-1">
                  {applicationInputs.applicationNumber || "N/A"}
                </div>
              </div>
              <div></div>
              <div>
                <label className="text-sm text-muted-foreground">
                  Yield
                </label>
                <div className="text-2xl font-bold mt-1 text-primary">
                  {metrics.yield}
                </div>
              </div>
            </div>

            <div className="border-t pt-4 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">
                    Application Score
                  </label>
                  <div className="text-2xl font-bold mt-1 text-primary">
                    {internalRating.score}
                    {internalRating.type && (
                      <span className="text-sm text-muted-foreground ml-2">
                        ({internalRating.type})
                      </span>
                    )}
                  </div>
                </div>
                <div></div>
                <div>
                  <label className="text-sm text-muted-foreground">
                    Loan Amount
                  </label>
                  <div className="text-2xl font-bold mt-1">
                    {formatCurrency(parseFloat(applicationInputs.loanAmount) || 0)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">
                    Application Rating
                  </label>
                  <div className={cn("text-2xl font-bold mt-1 text-primary")}>
                    {internalRating.rating}
                    {internalRating.decisionFlag && (
                      <Badge className="ml-2">
                        {internalRating.decisionFlag}
                      </Badge>
                    )}
                  </div>
                </div>
                <div></div>
                <div>
                  <label className="text-sm text-muted-foreground">
                    Approved Amount
                  </label>
                  <div className="text-2xl font-bold mt-1">
                    {formatCurrency(Number(applicationInputs.approvedAmount) || 0)}
                  </div>
                </div>
              </div>

              {(applicationInputs.pgAdvancePaymentSwitch || applicationInputs.corpAdvancePaymentSwitch) && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground">Deal-Structure Advance Payment</label>
                    <div className="text-lg font-bold mt-1 text-primary">Yes</div>
                  </div>
                </div>
              )}

              {(internalRating.decisionFlag || (applicationInputs.useCorpSwitch && parseFloat(applicationInputs.loanAmount) > 100000 && applicationInputs.corpRating !== "A1")) && (
                <Alert variant="default" className="[&>svg]:!text-primary">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {internalRating.decisionFlag === "Major Derog"
                      ? <span className="text-primary font-medium">Application Declined</span>
                      : applicationInputs.useCorpSwitch && parseFloat(applicationInputs.loanAmount) > 100000 && applicationInputs.corpRating !== "A1"
                        ? <span className="text-primary font-medium">Deal must score as A1 to be approved.</span>
                        : internalRating.decisionFlag === "Syndicate"
                          ? Number(applicationInputs.loanAmount || 0) > LOAN_AMOUNT_MAX
                            ? <span className="text-primary">Deals over $500,000 cannot be processed in Athena. This application is out of scope.</span>
                            : <span className="text-primary">This application may require syndication.</span>
                          : <span className="text-primary">Personal guarantor required for this application.</span>}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Right Side: Athena AI Recommendations */}
        <Card>
          <CardHeader>
            <CardTitle className="label-athena flex items-center gap-2">
              <Brain className="h-5 w-5" />
              Athena Recommendation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {applicationInputs.anyMajorDerog ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground">Recommendation</label>
                    <div className="text-2xl font-bold mt-1 text-muted-foreground">—</div>
                  </div>
                  <div />
                  <div>
                    <label className="text-sm text-muted-foreground">Target Yield</label>
                    <div className="text-2xl font-bold mt-1 text-muted-foreground">N/A</div>
                  </div>
                </div>
                <div className="border-t border-border my-4" />
                <div className="space-y-4 max-w-md">
                  <div>
                    <label className="text-sm text-muted-foreground">Risk Score</label>
                    <div className="text-2xl font-bold mt-1 text-muted-foreground">—</div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Risk Impact</label>
                    <div className="text-2xl font-bold mt-1 text-muted-foreground">—</div>
                  </div>
                </div>
              </div>
            ) : Number(applicationInputs.loanAmount || 0) > MODEL_SCORING_MAX ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground">Recommendation</label>
                    <div className="text-2xl font-bold mt-1 text-muted-foreground">—</div>
                  </div>
                  <div />
                  <div>
                    <label className="text-sm text-muted-foreground">Target Yield</label>
                    <div className="text-2xl font-bold mt-1 text-muted-foreground">N/A</div>
                  </div>
                </div>
                <div className="border-t border-border my-4" />
                <div className="space-y-4 max-w-md">
                  <div>
                    <label className="text-sm text-muted-foreground">Risk Score</label>
                    <div className="text-2xl font-bold mt-1 text-muted-foreground">—</div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Risk Impact</label>
                    <div className="text-2xl font-bold mt-1 text-muted-foreground">—</div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Athena model scoring covers loans up to $250,000.
                </p>
              </div>
            ) : (
            <>
            {!showRecommendations && !loading && (
              <div className="flex flex-col items-center justify-center py-12">
                <button
                  onClick={handleUnderwrite}
                  className="flex flex-col items-center justify-center pt-[14px] transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
                >
                  <img
                    src={require("../../../assets/images/icons/logo-large.png")}
                    alt="Athena AI"
                    className="h-[90px] w-[105px] cursor-pointer"
                  />
                  <span className="mt-2 text-sm text-primary">Click here</span>
                </button>
              </div>
            )}

            {loading && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="flex flex-col items-center justify-center pt-[14px]">
                  <img
                    src={require("../../../assets/images/icons/logo-large.png")}
                    alt="Athena AI"
                    className="h-[90px] w-[105px] animate-fade-in-out"
                  />
                  <p className="text-primary mt-2 text-center">
                    Reviewing application...
                  </p>
                </div>
              </div>
            )}

            {showRecommendations && (
              <div className="space-y-4 animate-fade-in">
                {/* Top Section: Recommendation and Yield */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground">
                      Recommendation
                    </label>
                    <div
                      className={cn(
                        "text-2xl font-bold mt-1",
                        getAthenaRecommendation() === "Approve" &&
                          "text-success",
                        getAthenaRecommendation() === "Decline" &&
                          "text-destructive",
                        getAthenaRecommendation() === "None" &&
                          "text-muted-foreground",
                      )}
                    >
                      {getAthenaRecommendation()}
                    </div>
                  </div>
                  <div></div>
                  <div>
                    <label className="text-sm text-muted-foreground">
                      Target Yield
                    </label>
                    <div className="text-2xl font-bold mt-1 text-primary">
                      N/A
                    </div>
                  </div>
                </div>

                {/* Stroke/Divider */}
                <div className="border-t border-border my-4" />

                {/* Below Section: Risk Score and Risk Impact */}
                <div className="space-y-4 max-w-md">
                  <div>
                    <label className="text-sm text-muted-foreground">
                      Risk Score
                    </label>
                    <div className="text-2xl font-bold mt-1 text-primary">
                      {metrics.riskScore}
                      <span className="text-xs font-normal text-muted-foreground ml-1">
                        {getRiskScoreHint()}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-muted-foreground">
                      Risk Impact
                    </label>
                    <div
                      className={cn(
                        "text-2xl font-bold mt-1",
                        (metrics.riskImpact === "CRITICAL" ||
                          metrics.riskImpact === "HIGH") && "text-destructive",
                        metrics.riskImpact === "MEDIUM" && "text-warning",
                        metrics.riskImpact === "LOW" && "text-success",
                      )}
                    >
                      {metrics.riskImpact}
                    </div>
                  </div>
                </div>
              </div>
            )}
            </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lookalike Table Section */}
      {showRecommendations && !applicationInputs.anyMajorDerog && (
        <Card>
          <CardHeader>
            <CardTitle className="label-athena text-center">Portfolio Look-alikes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center text-muted-foreground">
              {`Application number `}
              <strong className="text-foreground">
                {applicationInputs.applicationNumber}
              </strong>
              {` matches `}
              <strong
                className="text-foreground cursor-pointer hover:text-primary transition-colors"
                title="Show top matches by similarity"
                onClick={() => { setLookalikeFilter("default"); setRevealDetails(true); }}
              >
                {lookalikeCounts.total_matched || lookalikeAll.length || 0}
              </strong>
              {` contracts on our portfolio`}
              {lookalikeAll.length > 0 && (lookalikeCounts.total_matched || 0) > lookalikeAll.length && (
                <span>{`, showing top ${lookalikeAll.length}`}</span>
              )}
              {`.`}
            </div>

            <div className="border-t border-border my-4" />

            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => setRevealDetails(!revealDetails)}
              >
                {revealDetails ? "Hide Lookalikes" : "Show Lookalikes"}
              </Button>
            </div>

            {revealDetails && (
              <div className="space-y-4">
                {/* Lookalike summary metric cards */}
                {(() => {
                  const renderInnerCards = (cards: { title: string; value: string; sub: React.ReactNode | null; subColor?: string; color: string; filter: "default" | "performing" | "non_performing" | "rejected" | "syndicated" | "withdrawn"; clickable: boolean }[]) =>
                    cards.map(({ title, value, sub, subColor, color, filter, clickable }) => (
                      <Card
                        key={title}
                        className={`bg-background transition-all relative overflow-hidden min-h-[80px]${clickable ? " cursor-pointer" : ""}`}
                        onClick={() => { if (clickable) setLookalikeFilter(lookalikeFilter === filter ? "default" : filter); }}
                        title={clickable ? `Click to filter by ${title.toLowerCase()}` : undefined}
                      >
                        {clickable && lookalikeFilter === filter && (
                          <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-primary" />
                        )}
                        <CardContent className="flex flex-col items-center justify-center px-2 py-2 text-center h-full">
                          <label className={`text-sm text-muted-foreground leading-tight${clickable ? " cursor-pointer" : ""}`}>{title}</label>
                          <div className="text-sm font-bold leading-none mt-1" style={{ color }}>{value}</div>
                          {sub && <div className="text-sm font-semibold leading-none mt-0.5" style={{ color: subColor ?? "var(--muted-foreground)" }}>{sub}</div>}
                        </CardContent>
                      </Card>
                    ));
                  return (
                    <div className="grid grid-cols-7 gap-3">
                      {/* Funded Deals — spans 4 of 7 columns */}
                      <div className="col-span-4 rounded-lg border border-border p-3" style={{ backgroundColor: "rgb(50, 55, 62)" }}>
                        <p className="text-sm font-semibold label-athena mb-2 uppercase tracking-wider">Funded Deals</p>
                        <div className="grid grid-cols-4 gap-2">
                          {renderInnerCards([
                            {
                              title: "Performing",
                              value: activeStats.performing_pct !== null ? `${activeStats.performing_pct}%` : "—",
                              sub: activeStats.performing_count !== null ? `${activeStats.performing_count} deals` : null,
                              color: "var(--colorGreen)", filter: "performing" as const, clickable: true,
                            },
                            {
                              title: "Non-Performing",
                              value: activeStats.non_performing_pct !== null ? `${activeStats.non_performing_pct}%` : "—",
                              sub: activeStats.non_performing_count !== null ? `${activeStats.non_performing_count} deals` : null,
                              color: "#ff0000", filter: "non_performing" as const, clickable: true,
                            },
                            {
                              title: "Booked Amount",
                              value: activeStats.booked_amount !== null ? `$${activeStats.booked_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—",
                              sub: activeStats.funding_count !== null ? `${activeStats.funding_count} deals` : null,
                              color: "#ffffff", filter: "default" as const, clickable: false,
                            },
                            {
                              title: "Net Loss",
                              value: activeStats.net_loss !== null ? `$${activeStats.net_loss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—",
                              sub: activeStats.net_loss_pct !== null ? <><span style={{ color: "#ff0000" }}>{activeStats.net_loss_pct}%</span><span style={{ color: "#9ca3af" }}> life-to-loan loss</span></> : null,
                              subColor: "#ff0000",
                              color: "#ff0000", filter: "default" as const, clickable: false,
                            },
                          ])}
                        </div>
                      </div>
                      {/* Non-Funded Deals — spans 3 of 7 columns */}
                      <div className="col-span-3 rounded-lg border border-border p-3" style={{ backgroundColor: "rgb(50, 55, 62)" }}>
                        <p className="text-sm font-semibold label-athena mb-2 uppercase tracking-wider">Non-Funded Deals</p>
                        <div className="grid grid-cols-3 gap-2">
                          {renderInnerCards([
                            {
                              title: "Rejected",
                              value: activeStats.rejected_pct !== null ? `${activeStats.rejected_pct}%` : "—",
                              sub: activeStats.rejected_count !== null ? `${activeStats.rejected_count} deals` : null,
                              color: "#ffffff", filter: "rejected" as const, clickable: true,
                            },
                            {
                              title: "Syndicated",
                              value: activeStats.syndicated_pct !== null ? `${activeStats.syndicated_pct}%` : "—",
                              sub: activeStats.syndicated_count !== null ? `${activeStats.syndicated_count} deals` : null,
                              color: "#ffffff", filter: "syndicated" as const, clickable: true,
                            },
                            {
                              title: "Withdrawn",
                              value: activeStats.withdrawn_pct !== null ? `${activeStats.withdrawn_pct}%` : "—",
                              sub: activeStats.withdrawn_count !== null ? `${activeStats.withdrawn_count} deals` : null,
                              color: "#ffffff", filter: "withdrawn" as const, clickable: true,
                            },
                          ])}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {isFilterLoading && (
                  <p className="text-xs text-muted-foreground animate-pulse py-2 text-center">Applying filter…</p>
                )}

                {(() => {
                  const filterKey = lookalikeFilter === "performing"    ? "performing_matches"
                                 : lookalikeFilter === "non_performing" ? "non_performing_matches"
                                 : lookalikeFilter === "rejected"       ? "rejected_matches"
                                 : lookalikeFilter === "syndicated"     ? "syndicated_matches"
                                 : lookalikeFilter === "withdrawn"      ? "withdrawn_matches"
                                 : "all_similar";
                  const baseRows = lookalikeFilter === "performing"    ? lookalikePerforming
                                 : lookalikeFilter === "non_performing" ? lookalikeNonPerforming
                                 : lookalikeFilter === "rejected"       ? lookalikeRejected
                                 : lookalikeFilter === "syndicated"     ? lookalikeSyndicated
                                 : lookalikeFilter === "withdrawn"      ? lookalikeWithdrawn
                                 : lookalikeAll;
                  const filteredRows = filteredLookalike
                    ? (filteredLookalike[filterKey] ?? filteredLookalike.all_similar ?? [])
                    : baseRows;
                  return filteredRows.length > 0 ? (
                  <div className="border border-border rounded">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Application Number</TableHead>
                          {applicationInputs.usePGSwitch && (
                            <TableHead>FICO</TableHead>
                          )}
                          {applicationInputs.useCorpSwitch && (
                            <TableHead>PayNet</TableHead>
                          )}
                          {applicationInputs.useCorpSwitch && (
                            <TableHead>Paydex</TableHead>
                          )}
                          {applicationInputs.useCorpSwitch && (
                            <TableHead>Com Credit Score</TableHead>
                          )}
                          <TableHead>Submit Cost</TableHead>
                          <TableHead>Yrs In Bus</TableHead>
                          <TableHead>Employee Count</TableHead>
                          <TableHead>Num of Inquiries</TableHead>
                          <TableHead className="relative min-w-[120px]">
                            {assetTypeSearchOpen ? (
                              <div className="relative">
                                <input
                                  autoFocus
                                  value={assetTypeSearchText}
                                  onChange={e => { setAssetTypeSearchText(e.target.value); setAssetTypeHighlightIdx(-1); }}
                                  placeholder="Filter Asset Type"
                                  className="w-full text-xs border border-border rounded px-2 py-1 bg-background outline-none"
                                  onBlur={() => { if (!assetTypeSearchText && !activeFilterValue) setAssetTypeSearchOpen(false); }}
                                  onKeyDown={e => {
                                    if (!assetTypeSuggestions.length) return;
                                    if (e.key === "ArrowDown") { e.preventDefault(); setAssetTypeHighlightIdx(i => Math.min(i + 1, assetTypeSuggestions.length - 1)); }
                                    else if (e.key === "ArrowUp") { e.preventDefault(); setAssetTypeHighlightIdx(i => Math.max(i - 1, 0)); }
                                    else if (e.key === "Enter" && assetTypeHighlightIdx >= 0) {
                                      const val = assetTypeSuggestions[assetTypeHighlightIdx];
                                      setAssetTypeSearchText(val);
                                      setAssetTypeSuggestions([]);
                                      setAssetTypeHighlightIdx(-1);
                                      applyColumnFilter("assetType", val);
                                    }
                                  }}
                                />
                                {assetTypeSuggestions.length > 0 && (
                                  <div className="absolute z-50 top-full left-0 w-48 bg-popover border border-border rounded shadow-md max-h-48 overflow-y-auto">
                                    {assetTypeSuggestions.map((at: string, idx: number) => (
                                      <div
                                        key={at}
                                        className={`px-3 py-1.5 text-xs cursor-pointer ${idx === assetTypeHighlightIdx ? "bg-muted font-semibold" : "hover:bg-muted"}`}
                                        onMouseDown={() => {
                                          setAssetTypeSearchText(at);
                                          setAssetTypeSuggestions([]);
                                          setAssetTypeHighlightIdx(-1);
                                          applyColumnFilter("assetType", at);
                                        }}
                                      >{at}</div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <button
                                  className="flex items-center gap-1 text-xs font-medium hover:text-primary"
                                  onClick={() => setAssetTypeSearchOpen(true)}
                                >
                                  Asset Type {activeFilterType === "assetType" && activeFilterValue && <span className="text-primary">({activeFilterValue})</span>}
                                </button>
                                {activeFilterType === "assetType" && activeFilterValue && (
                                  <button
                                    className="text-muted-foreground hover:text-foreground text-xs leading-none"
                                    onClick={() => clearColumnFilter()}
                                  >✕</button>
                                )}
                              </div>
                            )}
                          </TableHead>
                          <TableHead className="relative min-w-[120px]">
                            {industrySearchOpen ? (
                              <div className="relative">
                                <input
                                  autoFocus
                                  value={industrySearchText}
                                  onChange={e => { setIndustrySearchText(e.target.value); setIndustryHighlightIdx(-1); }}
                                  placeholder="Filter Industry"
                                  className="w-full text-xs border border-border rounded px-2 py-1 bg-background outline-none"
                                  onBlur={() => { if (!industrySearchText && !activeFilterValue) setIndustrySearchOpen(false); }}
                                  onKeyDown={e => {
                                    if (!industrySuggestions.length) return;
                                    if (e.key === "ArrowDown") { e.preventDefault(); setIndustryHighlightIdx(i => Math.min(i + 1, industrySuggestions.length - 1)); }
                                    else if (e.key === "ArrowUp") { e.preventDefault(); setIndustryHighlightIdx(i => Math.max(i - 1, 0)); }
                                    else if (e.key === "Enter" && industryHighlightIdx >= 0) {
                                      const val = industrySuggestions[industryHighlightIdx];
                                      setIndustrySearchText(val);
                                      setIndustrySuggestions([]);
                                      setIndustryHighlightIdx(-1);
                                      applyColumnFilter("industry", val);
                                    }
                                  }}
                                />
                                {industrySuggestions.length > 0 && (
                                  <div className="absolute z-50 top-full left-0 w-48 bg-popover border border-border rounded shadow-md max-h-48 overflow-y-auto">
                                    {industrySuggestions.map((ind: string, idx: number) => (
                                      <div
                                        key={ind}
                                        className={`px-3 py-1.5 text-xs cursor-pointer ${idx === industryHighlightIdx ? "bg-muted font-semibold" : "hover:bg-muted"}`}
                                        onMouseDown={() => {
                                          setIndustrySearchText(ind);
                                          setIndustrySuggestions([]);
                                          setIndustryHighlightIdx(-1);
                                          applyColumnFilter("industry", ind);
                                        }}
                                      >{ind}</div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <button
                                  className="flex items-center gap-1 text-xs font-medium hover:text-primary"
                                  onClick={() => setIndustrySearchOpen(true)}
                                >
                                  Industry {activeFilterType === "industry" && activeFilterValue && <span className="text-primary">({activeFilterValue})</span>}
                                </button>
                                {activeFilterType === "industry" && activeFilterValue && (
                                  <button
                                    className="text-muted-foreground hover:text-foreground text-xs leading-none"
                                    onClick={() => clearColumnFilter()}
                                  >✕</button>
                                )}
                              </div>
                            )}
                          </TableHead>
                          <TableHead>Num Of Delinquencies</TableHead>
                          <TableHead>Default</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRows.map((row: any, idx: number) => (
                          <TableRow key={idx}>
                            <TableCell>
                              {row["Application Number"] ?? row.appId ?? "N/A"}
                            </TableCell>
                            {applicationInputs.usePGSwitch && (
                              <TableCell>{row.FICO_AVG != null ? Math.round(row.FICO_AVG) : "N/A"}</TableCell>
                            )}
                            {applicationInputs.useCorpSwitch && (
                              <TableCell>{row.PN_MASTER_SCR ?? "N/A"}</TableCell>
                            )}
                            {applicationInputs.useCorpSwitch && (
                              <TableCell>{row.PAYDEX_FIRM ?? "N/A"}</TableCell>
                            )}
                            {applicationInputs.useCorpSwitch && (
                              <TableCell>{row.COM_CRE_SCORE ?? "N/A"}</TableCell>
                            )}
                            <TableCell>{row["Submit Cost"] ?? "N/A"}</TableCell>
                            <TableCell>{row.LW_YRS_IN_BUS ?? "N/A"}</TableCell>
                            <TableCell>{row.EMP_TOT ?? "N/A"}</TableCell>
                            <TableCell>{row.NUM_INQ ?? "N/A"}</TableCell>
                            <TableCell>{row["Asset Type"] ?? "N/A"}</TableCell>
                            <TableCell>{row.Industry ?? "N/A"}</TableCell>
                            <TableCell>{row.WASDELINQ ?? "N/A"}</TableCell>
                            <TableCell>
                              {(lookalikeFilter === "rejected" || lookalikeFilter === "syndicated" || lookalikeFilter === "withdrawn") && !filteredLookalike
                                ? "N/A"
                                : (row.current_status_category === "Rejection" || row.current_status_category === "Syndication" || row.current_status_category === "Withdrawal")
                                ? "N/A"
                                : row.Default_TF === true
                                ? "Yes"
                                : row.Default_TF === false
                                ? "No"
                                : "N/A"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    No lookalike data available
                  </div>
                );
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Action Buttons — hidden for view-only (Accepted/Declined/CreditHold) loans.
          Deals over the model's range get them without an Athena run. */}
      {(showRecommendations || Number(applicationInputs.loanAmount || 0) > MODEL_SCORING_MAX || applicationInputs.anyMajorDerog) && !isTerminalStatus(loanStatus) && !isHoldStatus(loanStatus) && (
        <div className="flex flex-col items-center gap-4 pt-6 border-t">
          {(() => {
            const loanAmt = parseFloat(applicationInputs.loanAmount) || 0;
            const belowLimit = userCreditLimit !== null && loanAmt > (userCreditLimit ?? 0);
            // For Referred deals: only show decision buttons if the user has authority (within limit and <=$500k)
            const canDecide = loanAmt <= LOAN_AMOUNT_MAX && (loanStatus !== "Referred" || !belowLimit) && !applicationInputs.anyMajorDerog;
            return (
              <>
                {canDecide && userCreditLimit !== null && belowLimit && (
                  <p className="text-sm text-amber-600">
                    Your credit limit (${userCreditLimit.toLocaleString()}) is below this loan amount. You can only set this loan to Under Review.
                  </p>
                )}
                <div className="flex justify-center gap-4 flex-wrap">
                  {canDecide && (
                    <>
                      <Button
                        onClick={() => sendDecision("Declined")}
                        disabled={(!applicationInputs.anyMajorDerog && belowLimit) || isSubmitting}
                        variant="outline"
                        className="min-w-[130px] border-1 border-destructive text-destructive bg-card hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Decline
                      </Button>
                      <Button
                        onClick={() => sendDecision("CreditHold")}
                        disabled={isSubmitting || !!applicationInputs.anyMajorDerog}
                        variant="outline"
                        className="min-w-[130px] border-1 border-foreground text-foreground bg-card hover:bg-foreground hover:text-background hover:border-foreground"
                      >
                        <PauseCircle className="h-4 w-4 mr-1" />
                        Credit Hold
                      </Button>
                      <Button
                        onClick={() => sendDecision("Referred")}
                        disabled={isSubmitting || !!applicationInputs.anyMajorDerog}
                        variant="outline"
                        className="min-w-[130px] border-1 border-blue-400 text-blue-400 bg-card hover:bg-blue-400 hover:text-white hover:border-blue-400"
                      >
                        <CornerDownRight className="h-4 w-4 mr-1" />
                        Refer
                      </Button>
                      <Button
                        onClick={() => sendDecision("Accepted")}
                        disabled={
                          isSubmitting ||
                          belowLimit ||
                          Number(applicationInputs.loanAmount || 0) > LOAN_AMOUNT_MAX ||
                          internalRating.decisionFlag === "Major Derog"
                        }
                        variant="outline"
                        className="min-w-[130px] border-1 border-success text-success bg-card hover:bg-success hover:text-success-foreground hover:border-success"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Accept
                      </Button>
                    </>
                  )}
                  {applicationInputs.anyMajorDerog && (
                    <Button
                      onClick={() => sendDecision("Declined")}
                      disabled={isSubmitting}
                      variant="outline"
                      className="min-w-[130px] border-1 border-destructive text-destructive bg-card hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Decline
                    </Button>
                  )}
                  {!canDecide && loanAmt > LOAN_AMOUNT_MAX && !applicationInputs.anyMajorDerog && (
                    <>
                      <Button
                        onClick={() => sendDecision("Declined")}
                        disabled={isSubmitting}
                        variant="outline"
                        className="min-w-[130px] border-1 border-destructive text-destructive bg-card hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Decline
                      </Button>
                      <Button
                        onClick={() => sendDecision("CreditHold")}
                        disabled={isSubmitting}
                        variant="outline"
                        className="min-w-[130px] border-1 border-foreground text-foreground bg-card hover:bg-foreground hover:text-background hover:border-foreground"
                      >
                        <PauseCircle className="h-4 w-4 mr-1" />
                        Credit Hold
                      </Button>
                      <Button
                        onClick={() => sendDecision("Referred")}
                        disabled={isSubmitting}
                        variant="outline"
                        className="min-w-[130px] border-1 border-blue-400 text-blue-400 bg-card hover:bg-blue-400 hover:text-white hover:border-blue-400"
                      >
                        <CornerDownRight className="h-4 w-4 mr-1" />
                        Refer
                      </Button>
                    </>
                  )}
                  {(loanStatus === "Referred" || loanAmt > LOAN_AMOUNT_MAX || applicationInputs.anyMajorDerog) && (
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep("Loans")}
                      className="min-w-[130px] border-2 border-destructive text-destructive bg-card hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
                    >
                      Exit
                    </Button>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}
      {/* Referred deal before Athena is run (and not >250k, which is already handled above) */}
      {loanStatus === "Referred" && !showRecommendations && Number(applicationInputs.loanAmount || 0) <= MODEL_SCORING_MAX && (
        <div className="flex flex-col items-center gap-4 pt-6 border-t">
          <div className="flex justify-center gap-4 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setCurrentStep("Loans")}
              className="min-w-[130px] border-2 border-destructive text-destructive bg-card hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
            >
              Exit
            </Button>
          </div>
        </div>
      )}
      {isTerminalStatus(loanStatus) && (
        <div className="flex flex-col items-center gap-4 pt-6 border-t">
          <div className="flex justify-center gap-4 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setCurrentStep("Loans")}
              className="min-w-[130px] border-2 border-destructive text-destructive bg-card hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
            >
              Exit
            </Button>
          </div>
        </div>
      )}
      {isHoldStatus(loanStatus) && (
        <div className="flex flex-col items-center gap-4 pt-6 border-t">
          <div className="flex justify-center gap-4 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setCurrentStep("Loans")}
              className="min-w-[130px] border-2 border-destructive text-destructive bg-card hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
            >
              Exit
            </Button>
            <Button
              variant="outline"
              onClick={() => sendDecision("CreditReview")}
              className="min-w-[130px] border-2 border-secondary-foreground/60 text-secondary-foreground bg-card hover:bg-secondary hover:text-secondary-foreground hover:border-secondary-foreground/60"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Credit Review
            </Button>
          </div>
        </div>
      )}

      {pdfGenerating && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/45 backdrop-blur-xl backdrop-saturate-150"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="flex flex-col items-center gap-4 rounded-lg border border-border bg-card px-10 py-8 shadow-lg">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-base font-medium text-foreground">
              PDF generation in progress…
            </p>
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              Please wait while your report is prepared.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Underwrite;
